// How a presentation's items are grouped under titled sections.
//
// LIVES IN SHARED, AND THAT IS THE POINT. This is the logic a CLIENT sees —
// what gets a heading, what doesn't, and what happens when the data is odd.
// Keeping it as a pure function means the gate can EXECUTE every rule instead
// of pattern-matching JSX, which is the difference between a test that can
// fail and a test that looks like one.
//
// The rules exist because a presentation is sent to a paying client and the
// failure modes are all embarrassing rather than loud:
//
//   - Ungrouped items lead, with NO heading. Every presentation built before
//     sections existed has all-ungrouped items, so this is what makes the
//     feature invisible to anything already sent.
//   - A BLANK title renders no heading, not an empty bar. A rep creates a
//     section blank and names it afterwards; being interrupted in between must
//     not put a naked rule across the client's page.
//   - A section with no visible items is skipped. A dangling heading over
//     nothing reads as a mistake — which it is.
//   - An item whose section is MISSING is still shown, ungrouped. Losing a
//     product from the client's view is far worse than showing it without a
//     heading, and this is the only rule here that trades tidiness for safety.

export interface SectionLike {
  id: string;
  title: string;
  position: number;
}

export interface SectionItemLike {
  id: string;
  /** ABSENT and NULL mean the same thing: ungrouped.
   *
   *  The document surfaces (estimate / quote) type this OPTIONAL, because
   *  a lean caller can build a line without it. If "absent" were treated as
   *  anything other than ungrouped, those lines would fall through to the
   *  `__orphans` bucket and render at the BOTTOM of a document a client is
   *  reading — a silent reordering of a priced document, with no error anywhere.
   *  Hence `== null` at every check below rather than `=== null`. */
  sectionId?: string | null;
}

export interface PresentationGroup<TItem> {
  /** Stable react key. `__ungrouped` / `__orphans` are synthetic. */
  key: string;
  /** Heading to render, or null for "render these with no heading". */
  title: string | null;
  items: TItem[];
}

/** Group items for the client-facing render.
 *
 *  Pure and total: every visible item passed in comes back out in exactly one
 *  group. That property is what the gate asserts, because "an item silently
 *  disappeared from the client's presentation" is the failure that matters. */
export function groupItemsIntoSections<TItem extends SectionItemLike>(
  items: TItem[],
  sections: SectionLike[],
): PresentationGroup<TItem>[] {
  const ordered = [...sections].sort(
    (a, b) => a.position - b.position || a.id.localeCompare(b.id),
  );
  const groups: PresentationGroup<TItem>[] = [];

  // `== null` catches BOTH null and undefined — see SectionItemLike. An
  // absent sectionId must lead the document unheaded, never trail it.
  const ungrouped = items.filter((it) => it.sectionId == null);
  if (ungrouped.length > 0) {
    groups.push({ key: "__ungrouped", title: null, items: ungrouped });
  }

  for (const section of ordered) {
    const inSection = items.filter((it) => it.sectionId === section.id);
    if (inSection.length === 0) continue;
    groups.push({
      key: section.id,
      title: section.title.trim() === "" ? null : section.title,
      items: inSection,
    });
  }

  // Anything pointing at a section we weren't given. Shown, never dropped.
  const placed = new Set(groups.flatMap((g) => g.items.map((i) => i.id)));
  const orphans = items.filter((it) => !placed.has(it.id));
  if (orphans.length > 0) {
    groups.push({ key: "__orphans", title: null, items: orphans });
  }

  return groups;
}

/** A group on an ESTIMATE / SALES ORDER document. */
export interface DocumentGroup<TItem> {
  key: string;
  /** Heading to render, or null for "no heading" — blank titles blank out. */
  title: string | null;
  /** The real section behind this group; null for the synthetic buckets.
   *  The rep's editor needs the RAW stored title, which `title` hides when it
   *  is blank — otherwise a rep could never see the blank name to fix it. */
  section: SectionLike | null;
  items: TItem[];
}

/** Group document lines, with the ONE difference that matters between a rep
 *  editing and a client reading.
 *
 *  ⚠️ THE TRAP THIS FUNCTION EXISTS TO AVOID, and it has already been walked
 *  into once on the presentation side. The client-facing grouping deliberately
 *  SKIPS an empty section — a heading over nothing reads as a mistake. But a
 *  category is CREATED BLANK AND EMPTY: the rep clicks `+ Category`, and if the
 *  editor used the client rules that category would not exist on screen, so
 *  there would be nothing to type into and nothing to delete. During the Titles
 *  review both reviewers prescribed reusing the client function, and doing so
 *  would have introduced exactly that bug.
 *
 *  So: `editable` picks builder semantics (keep empty sections, surface unfiled
 *  lines) and read-only picks client semantics (skip empties). One function, so
 *  the rep and the client cannot drift, and so the gate can EXECUTE the
 *  difference rather than trusting two call sites to stay in step. */
export function groupLinesForDocument<TItem extends SectionItemLike>(
  lines: TItem[],
  sections: SectionLike[],
  opts: { editable: boolean },
): DocumentGroup<TItem>[] {
  const blankToNull = (t: string): string | null =>
    t.trim() === "" ? null : t;

  if (!opts.editable) {
    const byId = new Map(sections.map((s) => [s.id, s]));
    return groupItemsIntoSections(lines, sections).map((g) => ({
      key: g.key,
      title: g.title,
      section: byId.get(g.key) ?? null,
      items: g.items,
    }));
  }
  return groupItemsForBuilder(lines, sections).map((g) => ({
    key: g.key,
    title: g.section === null ? null : blankToNull(g.section.title),
    section: g.section,
    items: g.items,
  }));
}

/** What chrome a document renders above one group. */
export type SectionChrome =
  | { kind: "heading"; title: string }
  /** A REAL category the rep left unnamed — render the rule, no words.
   *  CEO 2026-08-07, verbatim: "If a category has no name - simply leave it
   *  blank in the presentation with just a line." */
  | { kind: "blank-heading" }
  | { kind: "caption" }
  | { kind: "none" };

/** Decide the chrome above a group on an ESTIMATE / SALES ORDER document.
 *
 *  LIVES HERE, AND THAT IS THE POINT — the same decision is made by THREE
 *  surfaces (the rep's document view, the rep's table view, and the client's
 *  /o/:uuid page). Written as a JSX condition it was three copies of
 *  `group.title !== null || hasSections`, and nothing could catch the day one
 *  copy changed. The rep and the client disagreeing about what is grouped on a
 *  document they are discussing is the failure this prevents.
 *
 *  The rules:
 *    - A titled group gets its heading.
 *    - An UNHEADED group gets the "Also included" caption, but ONLY when the
 *      document has sections at all. Both unheaded buckets qualify: the leading
 *      ungrouped run and `__orphans` (a line whose section row is missing).
 *      Without the caption, products sitting above "Drinkware" read to a client
 *      as "the first category lost its label".
 *    - A document with NO sections gets NOTHING — no heading, no caption, no
 *      band. That is every order created before sections existed, including the
 *      ones already sitting in a client's inbox, and they must render exactly as
 *      they did. This is the rule that makes the feature safe to ship. */
export function sectionChromeFor(
  group: { key: string; title: string | null },
  hasSections: boolean,
): SectionChrome {
  if (group.title !== null) return { kind: "heading", title: group.title };
  // A REAL category whose name is blank. `key` is the section's own id; only the
  // two synthetic buckets use the `__` prefix, so this is what separates "the
  // rep has not named this yet" from "these products are not in a category".
  //
  // ⚠️ THIS OVERRIDES THE ORIGINAL RULE, deliberately and on the CEO's word. The
  // old behaviour was "a blank title renders NO heading", written to stop a
  // half-finished category putting a naked rule across a client's page. He was
  // shown that and chose the rule anyway: an unnamed category between two named
  // ones rendering as nothing is worse — the products look like they fell out of
  // their group. A bare rule reads as a deliberate divider.
  if (!group.key.startsWith("__")) return { kind: "blank-heading" };
  if (hasSections) return { kind: "caption" };
  return { kind: "none" };
}

export interface BuilderGroup<TItem> {
  /** Stable react key. `__ungrouped` / `__unfiled` are synthetic. */
  key: string;
  /** The section this group belongs to; null for the two synthetic groups. */
  section: SectionLike | null;
  kind: "ungrouped" | "section" | "unfiled";
  items: TItem[];
}

/** Group items for the REP'S BUILDER. Deliberately not the same function as
 *  the client-facing one, and the differences are the whole reason it exists:
 *
 *    - An EMPTY section is KEPT. The client render skips it (a heading over
 *      nothing reads as a mistake), but the rep must see the section they just
 *      created blank, or they cannot name it or drop anything into it.
 *    - A BLANK title is NOT blanked out here. The builder renders an editable
 *      input, not a heading, so it needs the real stored value.
 *    - Items pointing at an unknown section come back in a dedicated
 *      `unfiled` group instead of being folded silently into ungrouped, so the
 *      builder can say plainly that something needs re-filing.
 *
 *  TOTAL, and that is the property worth testing: every item passed in comes
 *  back in exactly one group. The builder previously hand-filtered
 *  (`items.filter(i => i.sectionId === section.id)` per section, plus a
 *  null filter) with NO fallback bucket, so an item whose section was missing
 *  from the locally-cached sections list rendered NOWHERE — it vanished from
 *  the rep's own screen while still existing server-side. `sections` and
 *  `items` are two independent caches, so any lagged or failed refetch of one
 *  could do it. Both reviewers found this independently. */
export function groupItemsForBuilder<TItem extends SectionItemLike>(
  items: TItem[],
  sections: SectionLike[],
): BuilderGroup<TItem>[] {
  const ordered = [...sections].sort(
    (a, b) => a.position - b.position || a.id.localeCompare(b.id),
  );
  const groups: BuilderGroup<TItem>[] = [];

  // `== null` — both null and undefined are ungrouped. See SectionItemLike.
  const ungrouped = items.filter((it) => it.sectionId == null);
  if (ungrouped.length > 0) {
    groups.push({
      key: "__ungrouped",
      section: null,
      kind: "ungrouped",
      items: ungrouped,
    });
  }

  const known = new Set(ordered.map((s) => s.id));
  for (const section of ordered) {
    groups.push({
      key: section.id,
      section,
      kind: "section",
      items: items.filter((it) => it.sectionId === section.id),
    });
  }

  // The bucket whose absence was the bug. Non-null sectionId that matches no
  // section we know about — shown, and shown as needing attention.
  const unfiled = items.filter(
    (it) => it.sectionId != null && !known.has(it.sectionId),
  );
  if (unfiled.length > 0) {
    groups.push({
      key: "__unfiled",
      section: null,
      kind: "unfiled",
      items: unfiled,
    });
  }

  return groups;
}
